import { useState, useEffect } from 'react';
import { Layout } from '@/constants/layout';

interface UseScrollHeaderOptions {
  threshold?: number;
  hysteresis?: number;
}

interface UseScrollHeaderReturn {
  isVisible: boolean;
}

/**
 * Hook to detect when to show sticky header based on scroll position.
 * Uses hysteresis to prevent flickering when scroll is near threshold.
 *
 * @param options.threshold - Scroll position to show header (default: heroHeight)
 * @param options.hysteresis - Buffer zone to prevent flickering (default: 20px)
 */
export function useScrollHeader(
  options: UseScrollHeaderOptions = {}
): UseScrollHeaderReturn {
  const { threshold = Layout.heroHeight, hysteresis = 20 } = options;

  const getInitialVisibility = () => {
    if (typeof window === 'undefined') return false;
    return window.scrollY > threshold + hysteresis;
  };

  const [isVisible, setIsVisible] = useState(getInitialVisibility);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > threshold + hysteresis) {
        setIsVisible(true);
      } else if (currentScrollY < threshold - hysteresis) {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold, hysteresis]);

  return { isVisible };
}
